import {NEVER, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {Injectable} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {WSMessageReceiverService} from '@services/ws-message-receiver.service';
import {AiTasksStore} from '@stores/ai-tasks.store';

import {
  WebsocketMessageProcessor,
  WSMessageProcessorInput,
  WSMessageProcessorOutput,
} from './websocket-message-processor';

@Injectable()
export class AIScoresMessageProcessor implements WebsocketMessageProcessor {
  constructor(
    private wsMessageReceiverService: WSMessageReceiverService,
    private aiTasksStore: AiTasksStore,
  ) {}

  processMessage(
    params: WSMessageProcessorInput,
  ): Observable<WSMessageProcessorOutput> {
    const msg = params.msg;
    if (msg.int !== APIM.EventIntent.AIScoresGenerated) {
      return NEVER;
    }

    return this.wsMessageReceiverService
      .receive<APIM.EventData<PL2.AiScore>>(msg)
      .pipe(
        map((eventData) => {
          // drafted before the event succeeds, so a succeeded run always has its draft
          this._draftResult(msg.id, eventData?.c?.[0]);
          params.wsAPI.result(msg, eventData);
          return true;
        }),
      );
  }

  /**
   * Keeps the AI result on the AI task tracking the event, next to the score the teacher
   * picked while the AI streamed. Results of events no AI task tracks are not kept.
   */
  private _draftResult(trackingId: string, score: PL2.AiScore | undefined) {
    if (U.isEmpty(score?.score)) {
      return;
    }

    const [primaryPath, task] =
      Object.entries(this.aiTasksStore.state()).find(
        ([, task]) => !U.isEmpty(task.targetPKs?.[trackingId]),
      ) ?? [];
    if (!task) {
      return;
    }

    const targetPK = task.targetPKs[trackingId];
    const draft = task.draft?.[targetPK];
    const pick = draft?.type === 'autoscoring' ? draft.qF : undefined;
    this.aiTasksStore.storeDraft(primaryPath, targetPK, {
      type: 'autoscoring',
      aiQF: score.score,
      aiCmt: score.rationale,
      ...(U.isEmpty(pick) ? {} : {qF: pick}),
    });
  }
}
